import { Platform } from 'react-native';
import type { SummaryVisibility } from '../../utilities/visitRecordingApi';

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** What happens if the screen locks, in this build, on this device. */
export function lockNote(backgroundSupported: boolean | null, os: string = Platform.OS): { text: string; warn: boolean } {
  if (os === 'web') return { text: 'Keep this window open and in front while recording.', warn: true };
  if (backgroundSupported === false) {
    return { text: 'Keep the screen on: on this phone, recording stops if it locks. An app update will fix this.', warn: true };
  }
  return { text: 'Screen stays on while recording. If you lock your phone, recording keeps going.', warn: false };
}

/** How the recording ends without a tap: when they leave, if that's watched, and at the cap. */
export function autoStopNote(stopsWhenLeaving: boolean, maxMs: number): string {
  const hours = `${maxMs / 3_600_000} hours`;
  return stopsWhenLeaving ? `Stops by itself when you leave, or after ${hours}.` : `Stops by itself after ${hours}.`;
}

/** Who can see the summary, as the card's last line. */
export function visibilityLine(visibility: SummaryVisibility | null | undefined, nameOf?: (id: string) => string | null): string {
  if (!visibility || visibility.mode === 'admins') return 'Only you and the account’s owners can see this until you share it. It’s saved in the plan’s files.';
  if (visibility.mode === 'plan') return 'Shared with everyone on the plan. The transcript stays with you and the account’s owners.';
  const names = visibility.people.map((id) => nameOf?.(id)).filter(Boolean) as string[];
  const who = names.length && names.length <= 2 ? names.join(' and ') : `${visibility.people.length} people`;
  return `Shared with ${who}. The transcript stays with you and the account’s owners.`;
}

// The same rule as the server's appointments.doctorFromText: "Dr. Felix",
// "dr ana ruiz" → "Dr. Ana Ruiz". Used when the consent sheet was opened by a
// link, which carries only the event id.
const DOCTOR_RE = /\bdr\.?\s+([a-z][a-z'’-]+(?:\s+[a-z][a-z'’-]+)?)/i;
const NOT_A_SURNAME = new Set(['appointment', 'appt', 'visit', 'office', 'checkup', 'check', 'follow', 'followup', 'at', 'for', 'with', 'and']);

export function doctorFromText(text: unknown): string | null {
  const m = DOCTOR_RE.exec(String(text || ''));
  if (!m) return null;
  const words = m[1].split(/\s+/).filter((w) => !NOT_A_SURNAME.has(w.toLowerCase()));
  if (!words.length) return null;
  return `Dr. ${words.slice(0, 2).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}`;
}

/** The consent sheet's "Dr. Felix" and start time for one of the plan's calendar events. */
export function appointmentFromEvents(events: unknown, eventId: string): { label: string | null; startTime: string | null } | null {
  if (!Array.isArray(events)) return null;
  const row: any = events.find((e: any) => String(e?.event_id ?? e?.id ?? '') === eventId);
  if (!row) return null;
  const data = row.event_data || {};
  const label = doctorFromText(data.summary) || doctorFromText(data.description) || doctorFromText(data.location)
    || (typeof data.summary === 'string' && data.summary.trim() ? data.summary.trim().slice(0, 80) : null);
  return { label, startTime: data.start?.dateTime || null };
}

/** The share sheet's last line: says the recording is deleted only once the server confirmed it. */
export function recordingFateLine(audioDeleted: boolean): string {
  return audioDeleted
    ? 'The recording was deleted after transcription. Any delayed uploads are cleaned up automatically. The transcript stays with you and the account’s owners.'
    : 'The recording is being deleted now that it’s transcribed. The transcript stays with you and the account’s owners.';
}
